import React from 'react'
import {
  MotifButton,
  MotifChip,
  MotifIcon,
  MotifIconButton,
} from '@ey-xd/motif-react'
import {
  IconoirCheckCircle,
  IconoirInfoCircle,
  MotifNavigationIcClose24px,
  IconoirWarningTriangle,
  IconoirUpload,
  IconoirRefresh,
} from '@ey-xd/motif-icon'
import {
  useLazyDeleteEvidenceQuery,
} from '@/services/application/evidenceReducer'
import { EvidenceStatusEnum } from '@/services/types'
import { useDispatch, useSelector } from 'react-redux'
import { store } from '@/store'
import { setEvidence, setSupplementEvidence } from './DeveloperEvidenceSlice'

const getStatusType = (statusType: number) => {
  if (statusType > EvidenceStatusEnum.Temp_Storage) {
    return 'Successfully uploaded'
  }
  return 'Failed to upload'
}

const getAnnotationStatus = (
  statusType: number,
  supplementRequired: boolean,
  systemAccountsNumber: number
) => {
  if (supplementRequired === true && statusType !== EvidenceStatusEnum.Verified) {
    return (
      <div
        className="system-account-indicator-message"
        title={`${systemAccountsNumber} system ${systemAccountsNumber > 1 ? 'accounts' : 'account'} have been detected.`}
      >
        <MotifIcon src={IconoirInfoCircle} size="16" />{' '}
        <span>
          &nbsp; {systemAccountsNumber} system {systemAccountsNumber > 1 ? 'accounts' : 'account'} have been detected
        </span>
      </div>
    )
  }
  else if (statusType === EvidenceStatusEnum.Verified && systemAccountsNumber > 0) {
    return (
      <div
        className="verified-system-indicator-message"
        title={`Verified and ${systemAccountsNumber} system ${systemAccountsNumber > 1 ? 'accounts' : 'account'} have been detected`}
      >
        <MotifIcon src={IconoirCheckCircle} size="16" className="verified-system-indicator-icon" />
        <span>Verified and {systemAccountsNumber} system {systemAccountsNumber > 1 ? 'accounts' : 'account'} have been detected</span>
      </div>
    )
  }
  else if (statusType === EvidenceStatusEnum.Verified) {
    return 'Verified'
  }
  else if (statusType === EvidenceStatusEnum.MalwareScan) {
    return 'Awaiting scan'
  } else if (statusType === EvidenceStatusEnum.ScanFailed) {
    return (
      <div
        className="system-indicator-message"
        title="Malware scan failed. Please re-upload the evidence"
      >
        <MotifIcon src={IconoirWarningTriangle} size="16" />{' '}
        <span>&nbsp; Malware scan failed. Please re-upload the evidence</span>
      </div>
    )
  }
  return 'Yet To Be Verified'
}

//const extractAppID = () => {
//  const pathSegments = window.location.pathname.split('/')
//  return pathSegments[pathSegments.length - 1]
//}

const StatusCellRenderer = (props: any) => {
  return (
    <div style={{ alignSelf: 'center', display: 'flex', alignItems: 'center' }}>
      <MotifChip
        title="File Status"
        className={
          props.data.evidenceStatus > EvidenceStatusEnum.Temp_Storage
            ? 'upload-success'
            : 'upload-error'
        }
        type="dot"
        variant={
          props.data.evidenceStatus > EvidenceStatusEnum.Temp_Storage
            ? 'success'
            : 'error'
        }
      >
        <MotifIcon src={IconoirCheckCircle} size="16" />
        {getStatusType(props.data.evidenceStatus)}
      </MotifChip>
    </div>
  )
}

const AnnotationStatusCellRenderer = (props: any) => {
  const processingEvidenceIds = useSelector(
    (state: any) => state.annotationPolling.processingEvidenceIds as string[],
  )
  const pendingJobCountByEvidenceId = useSelector(
    (state: any) => state.annotationPolling.pendingJobCountByEvidenceId as Record<string, number>,
  )
  const isProcessing = processingEvidenceIds.includes(props.data.id)
  const pendingCount = pendingJobCountByEvidenceId[props.data.id] ?? 0
  const processingTitle = isProcessing
    ? `${pendingCount} annotation job${pendingCount !== 1 ? 's' : ''} pending…`
    : undefined
  const isVerified = props.data.evidenceStatus === EvidenceStatusEnum.Verified
  // Plain "Yet To Be Verified" (no supplement/scan issue) gets a warning
  // treatment (orange + triangle) instead of the generic error styling.
  const isYetToBeVerified =
    props.data.evidenceStatus === EvidenceStatusEnum.Yet_To_Be_Verified &&
    !props.data.supplementRequired

  return (
    <div
      style={{ alignSelf: 'center', display: 'flex', alignItems: 'center', gap: '6px' }}
      title={processingTitle}
    >
      <MotifChip
        title="Annotation Status"
        className={
          isVerified
            ? 'annotation-success'
            : isYetToBeVerified
              ? 'annotation-warning'
              : 'annotation-pending'
        }
        type="dot"
        variant={isVerified ? 'success' : isYetToBeVerified ? 'warning' : 'error'}
      >
        {isProcessing
          ? <span className="chip-spinner" />
          : !props.data.supplementRequired && (
            <MotifIcon
              src={
                isVerified
                  ? IconoirCheckCircle
                  : isYetToBeVerified
                    ? IconoirWarningTriangle
                    : IconoirInfoCircle
              }
              size="16"
            />
          )
        }
        {getAnnotationStatus(
          props.data.evidenceStatus,
          props.data.supplementRequired,
          props.data.systemAccounts.length
        )}
      </MotifChip>
    </div>
  )
}

const ActionCellRenderer = (props: any) => {
  const { supplementRequired, evidenceStatus } = props?.data ?? {}
  //const applicationId = extractAppID()
  const dispatch = useDispatch()
  const [deleteEvidence] = useLazyDeleteEvidenceQuery()

  const handleOpenAnnotation = (evidenceId: string) => {
    dispatch(
      setEvidence({
        isModalOpen: true,
        fileName: props.data.fileName,
        evidenceId,
      }),
    )
  }

  const handleDeleteEvidence = async (evidenceId: string) => {
    try {
      const response = await deleteEvidence(evidenceId).unwrap()
      if (response) {
        dispatch(
          setEvidence({
            isModalOpen: false,
            evidenceId: '',
            fileName: '',
            isRefreshRequired: true,
          }),
        )
        setTimeout(() => {
          dispatch(setEvidence({
            isModalOpen: false,
            evidenceId: '',
            fileName: '',
            isRefreshRequired: false,
          }))
        }, 500)
      }
    } catch (error) {
      console.error('Error deleting evidence:', error)
    }
  }

  const handleOpenSupplymentEvidence = (
    isSupplymentRequired: boolean,
    evidenceGroupId: string,
    supplementAccountName: string,
    supplementAccountId: string,
  ) => {
    dispatch(
      setSupplementEvidence({
        isSupplymentRequired: isSupplymentRequired,
        evidenceGroupId: evidenceGroupId,
        isSupplymentUploaded: false,
        supplementAccountName: supplementAccountName,
        supplementAccountId: supplementAccountId,
      }),
    )
  }

  return (
    <div style={{ display: 'flex', gap: '10px' }}>
      {(evidenceStatus === EvidenceStatusEnum.Yet_To_Be_Verified ||
        evidenceStatus === EvidenceStatusEnum.Verified) && (
          <MotifButton
            className="action-delete-button"
            size="small"
            variant={props.data.statusType === 1 ? 'primary' : 'secondary'}
            onClick={() => handleOpenAnnotation(props.data.id)}
          >
            View
          </MotifButton>
        )}
      {evidenceStatus === EvidenceStatusEnum.Error_Uploading && (
        <MotifButton
          className="action-delete-button"
          size="small"
          variant={props.data.statusType === 1 ? 'primary' : 'secondary'}
          onClick={() =>
            handleOpenSupplymentEvidence(true, props.data.evidenceGroupId, 'broken evidence', '')
          }
        >
          <MotifIcon iconFunction={IconoirRefresh} title="Reupload Evidence" />
          Reupload
        </MotifButton>
      )}
      {/* {supplementRequired && (
        <MotifButton
          className="action-delete-button"
          size="small"
          variant={props.data.statusType === 1 ? 'primary' : 'secondary'}
          onClick={() => {
            const account = props.data?.systemAccounts[0]
            handleOpenSupplymentEvidence(true, props.data.evidenceGroupId, account?.accountName, account?.id)
          }
          }
        >
          <MotifIcon iconFunction={IconoirUpload} title="Upload Evidence" />
          Upload
        </MotifButton>
      )} */}

      <MotifIconButton
        aria-label="Click here to see options"
        type="button"
        onClick={() => handleDeleteEvidence(props.data.id)}
      >
        <MotifIcon
          iconFunction={MotifNavigationIcClose24px}
          title="Delete Evidence"
        />
      </MotifIconButton>
    </div>
  )
}

export const useColumnDeveloperEvidenceListDefs = () => {
  return [
    {
      cellRenderer: 'developerReference',
      comparator: () => 0,
      field: 'fileName',
      type: 'developerReferenceType',
      headerName: 'File Name',
      flex: 1,
      headerHeight: 50,
      cellClass: 'text-wrap',
      rowGroup: true,
    },
    {
      comparator: () => 0,
      field: 'evidenceStatus',
      type: 'statusTypeReferenceType',
      headerName: 'File Upload Status',
      flex: 1,
      cellRenderer: StatusCellRenderer,
    },
    {
      // FE-only client sort by underlying status (not the raw enum string) so
      // "Yet To Be Verified" items can be sorted to the top/bottom.
      comparator: (_valueA: any, _valueB: any, nodeA: any, nodeB: any) =>
        (nodeA?.data?.evidenceStatus ?? 0) - (nodeB?.data?.evidenceStatus ?? 0),
      field: 'evidenceStatusName',
      type: 'statusTypeReferenceType',
      headerName: 'Annotation Status',
      cellClass: 'text-ellipsis',
      flex: 1,
      cellRenderer: AnnotationStatusCellRenderer,
      tooltipValueGetter: (params: any) => {
        const counts: Record<string, number> =
          (store.getState() as any).annotationPolling.pendingJobCountByEvidenceId ?? {}
        const count = counts[params.data?.id]
        if (!count) return undefined
        return `${count} annotation job${count !== 1 ? 's' : ''} pending…`
      },
    },
    {
      comparator: () => 0,
      field: 'numberOfUsers',
      type: 'statusTypeReferenceType',
      headerName: 'No Of User',
      flex: 1,
      cellRenderer: (props: any) => {
        return (
          <div className="align-center">
            {props.data.numberOfUsers > 0 ? props.data.numberOfUsers : '-'}
          </div>
        )
      },
    },
    {
      comparator: () => 0,
      field: 'environment',
      type: 'statusTypeReferenceType',
      headerName: 'Environment',
      flex: 0.5,
      cellClass: 'align-left ',
      cellRenderer: (props: any) => {
        return <div className="align-center">{props.data.environment}</div>
      },
    },
    {
      comparator: () => 0,
      field: 'statusType',
      type: 'statusTypeReferenceType',
      headerName: '',
      flex: 1,
      cellClass: 'align-right',
      cellRenderer: ActionCellRenderer,
    },
  ]
}
